import { useState, useEffect, useCallback, memo, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Avatar,
  TablePagination,
  Button,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  GroupAdd as BulkAddIcon,
  PersonOff as OfflinePlayerIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { offlinePlayersApi, connectionsApi } from '../../api';
import type { OfflinePlayer, PlayerConnectionWithStats } from '../../types';
import { renderIcon } from '../IconPicker';

// A selectable item can be either an offline player or a player connection
export type SelectablePlayer =
  | { type: 'offline'; player: OfflinePlayer }
  | { type: 'connection'; connection: PlayerConnectionWithStats };

interface ConnectionSelectorProps {
  selectedItem: SelectablePlayer | null;
  onSelect: (item: SelectablePlayer | null) => void;
  assignmentCounts: Map<string, number>;  // key is offlinePlayerId or connectionId
  onCreatePlayer: () => void;
  onBulkImport: () => void;
}

export const ConnectionSelector = memo<ConnectionSelectorProps>(({
  selectedItem,
  onSelect,
  assignmentCounts,
  onCreatePlayer,
  onBulkImport,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [offlinePlayers, setOfflinePlayers] = useState<OfflinePlayer[]>([]);
  const [connections, setConnections] = useState<PlayerConnectionWithStats[]>([]);
  const [loadingOffline, setLoadingOffline] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Load offline players on mount
  useEffect(() => {
    const loadOfflinePlayers = async () => {
      setLoadingOffline(true);
      try {
        const players = await offlinePlayersApi.getMyOfflinePlayers();
        setOfflinePlayers(players);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load offline players');
      } finally {
        setLoadingOffline(false);
      }
    };
    loadOfflinePlayers();
  }, []);

  // Load player connections on mount
  useEffect(() => {
    const loadConnections = async () => {
      setLoadingConnections(true);
      try {
        const conns = await connectionsApi.list();
        setConnections(conns);
      } catch (err) {
        // Connections may not be available - log for debugging but don't show error to user
        console.warn('Failed to load connections:', err instanceof Error ? err.message : err);
        setConnections([]);
      } finally {
        setLoadingConnections(false);
      }
    };
    loadConnections();
  }, []);

  // Filter locally based on search query
  const filteredOfflinePlayers = useMemo(() => {
    if (!searchQuery) return offlinePlayers;
    const query = searchQuery.toLowerCase();
    return offlinePlayers.filter(p =>
      p.displayName.toLowerCase().includes(query) ||
      (p.handle && p.handle.toLowerCase().includes(query))
    );
  }, [offlinePlayers, searchQuery]);

  const filteredConnections = useMemo(() => {
    if (!searchQuery) return connections;
    const query = searchQuery.toLowerCase();
    return connections.filter(c => {
      const user = c.connectedUser;
      if (!user) return false;
      return (
        user.displayName?.toLowerCase().includes(query) ||
        user.handle?.toLowerCase().includes(query) ||
        c.nickname?.toLowerCase().includes(query)
      );
    });
  }, [connections, searchQuery]);

  // Paginate
  const currentList = activeTab === 0 ? filteredOfflinePlayers : filteredConnections;
  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return currentList.slice(start, start + rowsPerPage);
  }, [currentList, page, rowsPerPage]);

  // Reset page when search or tab changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, activeTab]);

  const handleSelectOfflinePlayer = useCallback((player: OfflinePlayer) => {
    if (selectedItem?.type === 'offline' && selectedItem.player.id === player.id) {
      onSelect(null);
    } else {
      onSelect({ type: 'offline', player });
    }
  }, [selectedItem, onSelect]);

  const handleSelectConnection = useCallback((connection: PlayerConnectionWithStats) => {
    if (selectedItem?.type === 'connection' && selectedItem.connection.id === connection.id) {
      onSelect(null);
    } else {
      onSelect({ type: 'connection', connection });
    }
  }, [selectedItem, onSelect]);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const loading = activeTab === 0 ? loadingOffline : loadingConnections;
  const totalCount = activeTab === 0 ? offlinePlayers.length : connections.length;
  const filteredCount = currentList.length;

  return (
    <Box>
      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{ mb: 1, minHeight: 36 }}
      >
        <Tab
          icon={<OfflinePlayerIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Offline"
          sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
        />
        <Tab
          icon={<LinkIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          label="Connections"
          sx={{ minHeight: 36, py: 0.5, fontSize: '0.75rem' }}
        />
      </Tabs>

      {/* Header with add buttons - only show for offline players tab */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Add Player">
            <IconButton size="small" onClick={onCreatePlayer}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bulk Import">
            <IconButton size="small" onClick={onBulkImport}>
              <BulkAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Search field */}
      <TextField
        size="small"
        fullWidth
        placeholder={activeTab === 0 ? "Search offline players..." : "Search connections..."}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : null,
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Empty state for offline players */}
      {activeTab === 0 && !loading && offlinePlayers.length === 0 && (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No offline players yet. Create players to assign to template squares.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onCreatePlayer}
            sx={{ mr: 1 }}
          >
            Create
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<BulkAddIcon />}
            onClick={onBulkImport}
          >
            Bulk Import
          </Button>
        </Box>
      )}

      {/* Empty state for connections */}
      {activeTab === 1 && !loading && connections.length === 0 && (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No connections yet. Connections are created when players join your boards.
          </Typography>
        </Box>
      )}

      {/* No search results */}
      {!loading && filteredCount === 0 && totalCount > 0 && (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No results found for "{searchQuery}"
          </Typography>
        </Box>
      )}

      {/* List items */}
      {filteredCount > 0 && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {activeTab === 0 ? (
              // Offline players list
              (paginatedItems as OfflinePlayer[]).map((player) => {
                const count = assignmentCounts.get(player.id) || 0;
                const isSelected = selectedItem?.type === 'offline' && selectedItem.player.id === player.id;

                return (
                  <Box
                    key={player.id}
                    data-testid={`offline-player-item-${player.id}`}
                    onClick={() => handleSelectOfflinePlayer(player)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? 'white' : 'inherit',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: isSelected ? 'white' : 'grey.600',
                        color: isSelected ? 'primary.main' : 'white',
                        fontSize: 14,
                      }}
                    >
                      {player.iconName ? renderIcon(player.iconName, 18) : getInitials(player.displayName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {player.displayName}
                      </Typography>
                      {player.handle && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                          }}
                        >
                          @{player.handle.toUpperCase()}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : count > 0 ? 'primary.light' : 'grey.200',
                        color: isSelected ? 'white' : count > 0 ? 'primary.contrastText' : 'text.secondary',
                        height: 24,
                        minWidth: 32,
                      }}
                    />
                  </Box>
                );
              })
            ) : (
              // Connections list
              (paginatedItems as PlayerConnectionWithStats[]).map((connection) => {
                const count = assignmentCounts.get(connection.id) || 0;
                const isSelected = selectedItem?.type === 'connection' && selectedItem.connection.id === connection.id;
                const user = connection.connectedUser;
                const displayName = connection.nickname || user?.displayName || 'Unknown';
                const handle = user?.handle;

                return (
                  <Box
                    key={connection.id}
                    data-testid={`connection-item-${connection.id}`}
                    onClick={() => handleSelectConnection(connection)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'secondary.main' : 'transparent',
                      color: isSelected ? 'white' : 'inherit',
                      '&:hover': {
                        bgcolor: isSelected ? 'secondary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: isSelected ? 'white' : 'secondary.main',
                        color: isSelected ? 'secondary.main' : 'white',
                        fontSize: 14,
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {displayName}
                      </Typography>
                      {handle && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                          }}
                        >
                          @{handle.toUpperCase()}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : count > 0 ? 'secondary.light' : 'grey.200',
                        color: isSelected ? 'white' : count > 0 ? 'secondary.contrastText' : 'text.secondary',
                        height: 24,
                        minWidth: 32,
                      }}
                    />
                  </Box>
                );
              })
            )}
          </Box>

          {/* Pagination */}
          {filteredCount > rowsPerPage && (
            <TablePagination
              component="div"
              count={filteredCount}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              sx={{
                '.MuiTablePagination-toolbar': { minHeight: 40, px: 0 },
                '.MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
              }}
            />
          )}
        </>
      )}

      {selectedItem && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Click squares to assign. Click their squares to unassign.
          </Typography>
          <Button
            size="small"
            fullWidth
            onClick={() => onSelect(null)}
            sx={{ mt: 1 }}
          >
            Done Assigning
          </Button>
        </Box>
      )}
    </Box>
  );
});

ConnectionSelector.displayName = 'ConnectionSelector';
